import { Button } from '@openai/apps-sdk-ui/components/Button';
import { Moon, Sun } from '@openai/apps-sdk-ui/components/Icon';
import {
  applyDocumentTheme,
  useDocumentTheme,
  type DocumentTheme,
} from '@openai/apps-sdk-ui/theme';

export const THEME_STORAGE_KEY = 'terminal.theme';
const validTheme = (value: string | null): DocumentTheme => (value === 'light' ? 'light' : 'dark');
function applyTheme(theme: DocumentTheme) {
  applyDocumentTheme(theme);
  document
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'light' ? '#f9f9f9' : '#181818');
}
export function initializeTheme() {
  let theme: DocumentTheme = 'dark';
  try {
    theme = validTheme(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    /* Appearance still works when browser storage is unavailable. */
  }
  applyTheme(theme);
  window.addEventListener('storage', (event) => {
    if (event.key === THEME_STORAGE_KEY || event.key === null)
      applyTheme(validTheme(event.newValue));
  });
}
function chooseTheme(theme: DocumentTheme) {
  applyTheme(theme);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* Keep the selected appearance for this page. */
  }
}
export function ThemeToggle() {
  const theme = useDocumentTheme();
  const label = theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
  return (
    <Button
      className="appearance-toggle"
      color="secondary"
      variant="ghost"
      uniform
      size="sm"
      aria-label={label}
      title={label}
      onClick={() => chooseTheme(theme === 'dark' ? 'light' : 'dark')}
    >
      {theme === 'dark' ? <Sun /> : <Moon />}
    </Button>
  );
}
export function AppearanceControls() {
  const theme = useDocumentTheme();
  return (
    <section className="settings-section appearance-section">
      <div className="settings-section-heading">
        <h2>Appearance</h2>
        <p>Choose how Terminal looks in this browser.</p>
      </div>
      <div className="appearance-options" role="group" aria-label="Appearance">
        <Button
          color="secondary"
          variant={theme === 'light' ? 'soft' : 'outline'}
          aria-pressed={theme === 'light'}
          onClick={() => chooseTheme('light')}
        >
          <Sun />
          Light
        </Button>
        <Button
          color="secondary"
          variant={theme === 'dark' ? 'soft' : 'outline'}
          aria-pressed={theme === 'dark'}
          onClick={() => chooseTheme('dark')}
        >
          <Moon />
          Dark
        </Button>
      </div>
    </section>
  );
}
